import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, 
  Package, 
  User, 
  Calendar,
  ChevronLeft,
  ChevronRight,
  Trash2,
  RotateCcw,
  Eye,
  Filter,
  X,
  ExternalLink,
  ImageOff
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  const [selectedListing, setSelectedListing] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    fetchListings();
  }, [pagination.skip, statusFilter]);

  useEffect(() => { setSelectedIds(new Set()); }, [pagination.skip, statusFilter, search]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const idsOnPage = listings.map(l => l.id);
  const allSelectedOnPage = idsOnPage.length > 0 && idsOnPage.every(id => selectedIds.has(id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) idsOnPage.forEach(id => next.delete(id));
      else idsOnPage.forEach(id => next.add(id));
      return next;
    });
  };

  const bulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    let reason = 'Bulk admin action';
    if (action === 'remove') {
      reason = window.prompt(`Remove ${ids.length} listing(s)? Enter a reason:`, 'Policy violation') || '';
      if (!reason.trim()) return;
    } else if (!window.confirm(`Restore ${ids.length} listing(s)?`)) {
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/listings/bulk-action`, {
        listing_ids: ids, action, reason,
      });
      toast.success(res.data.message || 'Bulk action complete');
      setSelectedIds(new Set());
      fetchListings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setPagination(prev => ({ ...prev, skip: 0 }));
      fetchListings();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: pagination.skip,
        limit: pagination.limit
      });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API_URL}/api/admin/listings?${params}`);
      setListings(response.data.listings);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      toast.error('Failed to fetch listings');
    } finally {
      setLoading(false);
    }
  };

  const handleViewListing = (listing) => {
    setSelectedListing(listing);
    setShowDetailsModal(true);
  };

  const handleRemoveListing = (listing) => {
    setSelectedListing(listing);
    setRemoveReason('');
    setShowRemoveModal(true);
  };

  const confirmRemoveListing = async () => {
    if (!removeReason.trim()) {
      toast.error('Please provide a reason for removal');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/listings/${selectedListing.id}/remove`, {
        reason: removeReason
      });
      toast.success('Listing removed successfully');
      setShowRemoveModal(false);
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove listing');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestoreListing = async (listing) => {
    if (!confirm(`Restore listing "${listing.title}"?`)) return;

    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/listings/${listing.id}/restore`);
      toast.success('Listing restored successfully');
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore listing');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      live: 'bg-emerald-500/20 text-emerald-400',
      closed: 'bg-slate-500/20 text-slate-400',
      removed: 'bg-red-500/20 text-red-400',
      in_trade: 'bg-amber-500/20 text-amber-400',
      completed: 'bg-blue-500/20 text-blue-400',
      cancelled: 'bg-slate-500/20 text-slate-400',
      draft: 'bg-slate-500/20 text-slate-400'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-slate-500/20 text-slate-400'}`}>
        {status?.replace('_', ' ')?.toUpperCase()}
      </span>
    );
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Listing Management
        </h1>
        <p className="text-slate-400">View and manage all platform listings (ads)</p>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-2xl p-4 mb-6 border border-slate-700 flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or description..."
            className="w-full pl-12 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            data-testid="listing-search-input"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-12 pr-8 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none cursor-pointer"
            data-testid="status-filter-select"
          >
            <option value="">All Statuses</option>
            <option value="live">Live</option>
            <option value="closed">Closed</option>
            <option value="removed">Removed</option>
            <option value="in_trade">In Trade</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="bg-indigo-600/20 border border-indigo-500/40 rounded-xl px-4 py-3 mb-4 flex items-center justify-between" data-testid="listings-bulk-bar">
          <p className="text-sm text-indigo-200">
            <strong className="text-white">{selectedIds.size}</strong> listing{selectedIds.size > 1 ? 's' : ''} selected
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => bulkAction('remove')}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-remove-listings-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Remove Selected
            </Button>
            <Button
              onClick={() => bulkAction('restore')}
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bulk-restore-listings-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Restore Selected
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedIds(new Set())}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Listings Table */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <Package className="w-12 h-12 mx-auto mb-2 text-slate-600" />
            <p>No listings found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-4 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={allSelectedOnPage}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded accent-indigo-600"
                      data-testid="select-all-listings"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Listing</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Owner</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Listed Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {listings.map((listing) => (
                  <tr 
                    key={listing.id} 
                    className="hover:bg-slate-700/30 transition-colors"
                    data-testid={`listing-row-${listing.id}`}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(listing.id)}
                        onChange={() => toggleSelect(listing.id)}
                        className="w-4 h-4 rounded accent-indigo-600"
                        data-testid={`select-listing-${listing.id}`}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
                          {listing.images?.[0] ? (
                            <img 
                              src={listing.images[0]} 
                              alt={listing.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`${listing.images?.[0] ? 'hidden' : 'flex'} w-full h-full items-center justify-center`}>
                            <ImageOff className="w-5 h-5 text-slate-500" />
                          </div>
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate max-w-xs" title={listing.title}>
                            {listing.title}
                          </p>
                          <p className="text-sm text-slate-400 truncate max-w-xs">
                            {listing.city && listing.state ? `${listing.city}, ${listing.state}` : 'Location not set'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">
                            {listing.owner_name?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm truncate">{listing.owner_name || 'Unknown'}</p>
                          <p className="text-xs text-slate-400 truncate">{listing.owner_email || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-300 text-sm">{listing.category || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        {formatDate(listing.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(listing.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewListing(listing)}
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                          title="View Details"
                          data-testid={`view-listing-${listing.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {listing.status === 'removed' ? (
                          <button
                            onClick={() => handleRestoreListing(listing)}
                            className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-colors"
                            title="Restore Listing"
                            data-testid={`restore-listing-${listing.id}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRemoveListing(listing)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Remove Listing"
                            data-testid={`remove-listing-${listing.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        <a
                          href={`/listing/${listing.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                          title="Open in App"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} listings
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
              disabled={pagination.skip === 0}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-slate-400 text-sm">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
              disabled={pagination.skip + pagination.limit >= pagination.total}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Listing Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white" data-testid="listing-details-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center overflow-hidden">
                {selectedListing?.images?.[0] ? (
                  <img 
                    src={selectedListing.images[0]} 
                    alt={selectedListing?.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package className="w-6 h-6 text-slate-500" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  {selectedListing?.title}
                  {getStatusBadge(selectedListing?.status)}
                </div>
                <p className="text-sm text-slate-400">by {selectedListing?.owner_name}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedListing && (
            <div className="mt-4 space-y-6">
              {/* Listing Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-1">Category</p>
                  <p className="text-white">{selectedListing.category || 'N/A'}</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-1">Condition</p>
                  <p className="text-white capitalize">{selectedListing.condition?.replace('_', ' ') || 'N/A'}</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-1">Location</p>
                  <p className="text-white">
                    {[selectedListing.city, selectedListing.state, selectedListing.country].filter(Boolean).join(', ') || 'N/A'}
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-1">Listed Date</p>
                  <p className="text-white">{formatDate(selectedListing.created_at)}</p>
                </div>
              </div>

              {/* Description */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <p className="text-slate-400 text-sm mb-2">Description</p>
                <p className="text-white text-sm whitespace-pre-wrap">{selectedListing.description || 'No description provided'}</p>
              </div>

              {/* Bidding End Time */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <p className="text-slate-400 text-sm mb-1">Bidding Ends</p>
                <p className="text-white">{formatDate(selectedListing.bidding_end_time)}</p>
              </div>

              {/* Owner Info */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <p className="text-slate-400 text-sm mb-2">Owner Details</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">{selectedListing.owner_name?.[0]?.toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="text-white">{selectedListing.owner_name}</p>
                    <p className="text-sm text-slate-400">{selectedListing.owner_email}</p>
                  </div>
                </div>
              </div>

              {/* Removal info if removed */}
              {selectedListing.status === 'removed' && selectedListing.removed_reason && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <p className="text-red-400 font-medium mb-1">Removal Reason:</p>
                  <p className="text-slate-300">{selectedListing.removed_reason}</p>
                  {selectedListing.removed_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      Removed on {formatDate(selectedListing.removed_at)}
                    </p>
                  )}
                </div>
              )}

              {/* Images */}
              {selectedListing.images?.length > 0 && (
                <div>
                  <p className="text-slate-400 text-sm mb-2">Images ({selectedListing.images.length})</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedListing.images.slice(0, 6).map((img, idx) => (
                      <div key={idx} className="aspect-square rounded-xl overflow-hidden bg-slate-700">
                        <img src={img} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove Listing Modal */}
      <Dialog open={showRemoveModal} onOpenChange={setShowRemoveModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white" data-testid="remove-listing-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Remove Listing
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <p className="text-slate-300">
              Are you sure you want to remove the listing <strong>"{selectedListing?.title}"</strong>? 
              The owner will be notified of this action.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Reason for removal *</label>
              <textarea
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                placeholder="Provide a reason for removing this listing..."
                className="w-full bg-slate-700 border border-slate-600 rounded-xl p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-red-500"
                rows={3}
                data-testid="remove-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRemoveModal(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRemoveListing}
                disabled={actionLoading || !removeReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-remove-btn"
              >
                {actionLoading ? 'Removing...' : 'Remove Listing'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
